import { useEffect, useRef, useState } from 'react';

interface TranscriptGroup {
    start: number;
    end: number;
}

const useAudioPlayer = (audioUrl: string, transcript: TranscriptGroup[][], onEndNavigate: () => void) => {
    const audioRef = useRef<HTMLAudioElement>(null);
    const [currentTime, setCurrentTime] = useState(0);
    const [currentGroup, setCurrentGroup] = useState(0);
    const [isEnded, setIsEnded] = useState(false);
    let inactivityTimer: NodeJS.Timeout | null = null;

    const resetInactivityTimer = () => {
        if (inactivityTimer) clearTimeout(inactivityTimer);
        inactivityTimer = setTimeout(() => onEndNavigate(), 90000);
    };

    useEffect(() => {
        const audioElement = audioRef.current;
        if (!audioElement) return;

        const handleAudioEnded = () => {
            resetInactivityTimer();
            setIsEnded(true);
            audioElement.currentTime = 0;
            audioElement.pause();
        };

        const handleAudioPlay = () => setIsEnded(false);
        const handleTouch = () => resetInactivityTimer();

        audioElement.addEventListener('ended', handleAudioEnded);
        audioElement.addEventListener('play', handleAudioPlay);
        document.addEventListener('touchstart', handleTouch);

    }, [isEnded]);

    useEffect(() => {
        const audioElement = audioRef.current;
        if (!audioElement) return;

        const updateTime = () => setCurrentTime(audioElement.currentTime);
        audioElement.addEventListener('timeupdate', updateTime);
        audioElement.play().catch(console.log);

        return () => audioElement.removeEventListener('timeupdate', updateTime);
    }, [audioUrl]);

    useEffect(() => {
        if (!audioRef.current || !transcript) return;

        const currentGroupIndex = transcript.findIndex((group) => {
            const [groupStart, groupEnd] = [group[0].start, group[group.length - 1].end];
            return currentTime >= groupStart && currentTime < groupEnd;
        });
        if (currentGroupIndex !== -1) setCurrentGroup(currentGroupIndex);
    }, [currentTime, transcript]);

    const seekTo = (time: number) => {
        if (audioRef.current) {
            audioRef.current.currentTime = time;
            audioRef.current.play();
        }
    };

    return { audioRef, currentTime, currentGroup, seekTo, isEnded };
};

export default useAudioPlayer;